//@flow
import * as Immutable from 'immutable';

import PjpInsightsActions from 'common/redux/actions/pjpInsights';
import { PjpInsightsDashboard } from 'common/models/internal/PjpInsightsDashboard';
import { PjpMetricResponse } from 'common/models/internal/PjpMetricResponse';
import { PjpInsightsMetadata } from 'common/models/internal/PjpInsightsMetadata';

export const pjpInsights = (
    state = Immutable.fromJS({
        allPjpDashboards: {}
    }),
    action
) => {
    switch (action.type) {
        case PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_START: {
            const path = ['allPjpDashboards'];
            let pjpDashboardsMap = Immutable.OrderedMap();
            return state.withMutations(mutableState => {
                mutableState.setIn([...path], pjpDashboardsMap);
                mutableState.setIn([...path, 'isLoading'], true);
            });
        }
        case PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_SUCCESS: {
            const path = ['allPjpDashboards'];
            let pjpDashboardsMap = Immutable.OrderedMap();
            const dashboards = action.entity;
            return state.withMutations(mutableState => {
                dashboards.forEach(dashboard => {
                    pjpDashboardsMap = pjpDashboardsMap.set(
                        dashboard.dashboardId,
                        PjpInsightsDashboard.fromJS(dashboard)
                    );
                });
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path], pjpDashboardsMap);
            });
        }

        case PjpInsightsActions.FETCH_ALL_PJP_DASHBOARDS_FAILURE: {
            const path = ['allPjpDashboards'];
            const { error } = action;
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'error'], error);
                mutableState.setIn([...path, 'isLoading'], false);
            });
        }

        case PjpInsightsActions.FETCH_PJP_METRICS_METADATA_START: {
            const path = ['metricsMetadata'];
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], true);
            });
        }
        case PjpInsightsActions.FETCH_PJP_METRICS_METADATA_SUCCESS: {
            const path = ['metricsMetadata'];
            const { metricsMetadata } = action;

            const pjpMetadata = metricsMetadata.reduce(
                (pjpMetadataMap, metadata) => {
                    pjpMetadataMap[
                        metadata.metric
                    ] = PjpInsightsMetadata.fromJS(metadata);
                    return pjpMetadataMap;
                },
                {}
            );

            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn(
                    [...path, 'metadata'],
                    Immutable.Map(pjpMetadata)
                );
            });
        }
        case PjpInsightsActions.FETCH_PJP_METRICS_METADATA_FAILURE: {
            const path = ['metricsMetadata'];
            const { error } = action;
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'error'], error);
            });
        }
        case PjpInsightsActions.FETCH_PJP_METRIC_START: {
            const { params } = action;
            const { flushExisting } = params;
            const path = ['pjpMetricData', params.pjpId, params.graphId];
            const entity = flushExisting
                ? null
                : state.getIn([...path, 'entity']);
            const queries = state.getIn([...path, 'queries']);
            const isRefreshing = entity ? true : false;
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], true);
                mutableState.setIn([...path, 'isRefreshing'], isRefreshing);
                mutableState.setIn([...path, 'hasData'], entity ? true : false);
                mutableState.setIn([...path, 'queries'], queries || null);
                mutableState.setIn([...path, 'entity'], entity || null);
            });
        }
        case PjpInsightsActions.FETCH_PJP_METRIC_SUCCESS: {
            const { metricResponse, queries, params } = action;
            const path = ['pjpMetricData', params.pjpId, params.graphId];

            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'queries'], queries);
                mutableState.setIn([...path, 'isRefreshing'], false);
                mutableState.setIn([...path, 'hasData'], true);
                mutableState.setIn(
                    [...path, 'entity'],
                    PjpMetricResponse.fromJS({
                        metricsData: metricResponse.metricsData
                    })
                );
            });
        }
        case PjpInsightsActions.FETCH_PJP_METRIC_FAILURE: {
            const { error, queries, params } = action;
            const path = ['pjpMetricData', params.pjpId, params.graphId];

            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'queries'], queries);
                mutableState.setIn([...path, 'isRefreshing'], false);
                mutableState.setIn([...path, 'hasData'], false);
                mutableState.setIn([...path, 'error'], error);
                mutableState.setIn([...path, 'entity'], null);
            });
        }

        case PjpInsightsActions.CLEAR_CONFIGURED_METRIC: {
            const path = ['configuredMetric'];
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'queries'], null);
                mutableState.setIn([...path, 'entity'], null);
                mutableState.setIn([...path, 'hasData'], false);
                mutableState.setIn([...path, 'currentPlan'], null);
                mutableState.setIn([...path, 'existingPlan'], null);
            });
        }

        case PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_START: {
            const path = ['configuredMetric'];
            const { queries } = action;
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], true);
                mutableState.setIn([...path, 'queries'], queries);
                mutableState.setIn([...path, 'entity'], null);
                mutableState.setIn([...path, 'hasData'], false);
                mutableState.setIn([...path, 'currentPlan'], null);
                mutableState.setIn([...path, 'existingPlan'], null);
            });
        }

        case PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_SUCCESS: {
            const { queries } = action;

            const path = ['configuredMetric'];

            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'queries'], queries);
                const pjpMetricRequest = PjpMetricResponse.fromJS(
                    action.metricResponse
                );
                const currentPlanData = pjpMetricRequest.metricsData.get(0)
                    .currentPlan;
                const existingPlanData = pjpMetricRequest.metricsData.get(0)
                    .existingPlan;

                mutableState.setIn([...path, 'entity'], pjpMetricRequest);
                mutableState.setIn([...path, 'hasData'], true);
                mutableState.setIn([...path, 'currentPlan'], currentPlanData);
                mutableState.setIn([...path, 'existingPlan'], existingPlanData);
            });
        }
        case PjpInsightsActions.FETCH_CONFIGURED_PJP_METRIC_FAILURE: {
            const path = ['configuredMetric'];
            const { error, queries } = action;
            return state.withMutations(mutableState => {
                mutableState.setIn([...path, 'error'], error);
                mutableState.setIn([...path, 'isLoading'], false);
                mutableState.setIn([...path, 'queries'], queries);
                mutableState.setIn([...path, 'entity'], null);
                mutableState.setIn([...path, 'hasData'], false);
                mutableState.setIn([...path, 'currentPlan'], null);
                mutableState.setIn([...path, 'existingPlan'], null);
            });
        }
    }
    return state;
};
